import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AddAccountModal from "./AddAccountModal";
import api from "../api/api"; // Axios instance config
import Toast, { useToast } from "../components/Toast";
import ConfirmDialog, { useConfirm } from "./ConfirmDialog";

import {
  Mail,
  Pencil,
  MessageCircle,
  Download,
  Trash2,
  Phone,
  Loader2,
  Upload,
  RefreshCw,
  MoreHorizontal,
  Building2,
  CheckCircle2,
  XCircle,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Filter,
  Users,
  X,
} from "lucide-react";

// Small searchable multi-select used by the filter panel (type to search,
// pick from the dropdown, remove via the chip's x). Mirrors the same
// component used on the Opportunities page so filtering feels consistent
// across list pages.
function MultiSelectFilterField({ label, placeholder, options, selectedValues, onChange, onClear }) {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const filteredOptions = options.filter(
    (opt) => !selectedValues.includes(opt) && opt.toLowerCase().includes(search.toLowerCase())
  );

  const addValue = (value) => {
    onChange([...selectedValues, value]);
    setSearch("");
  };

  const removeValue = (value) => {
    onChange(selectedValues.filter((v) => v !== value));
  };

  return (
    <div className="mb-3">
      <div className="mb-1.5 flex items-center justify-between">
        <p className="text-xs font-bold text-slate-600">{label}</p>
        {onClear && selectedValues.length > 0 && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClear}
            className="text-[11px] font-semibold text-blue-600 hover:text-blue-700 cursor-pointer"
          >
            Clear
          </button>
        )}
      </div>

      {selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {selectedValues.map((value) => (
            <span
              key={value}
              className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100 pl-2 pr-1 py-0.5 text-[11px] font-medium max-w-full"
            >
              <span className="truncate">{value}</span>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => removeValue(value)}
                className="shrink-0 text-blue-400 hover:text-blue-700 cursor-pointer"
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <input
          type="text"
          placeholder={placeholder}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setIsOpen(false)}
          className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        {isOpen && filteredOptions.length > 0 && (
          <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-20 max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg py-1">
            {filteredOptions.map((opt) => (
              <div
                key={opt}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => addValue(opt)}
                className="px-2.5 py-1.5 text-sm text-slate-700 hover:bg-blue-50 cursor-pointer truncate"
              >
                {opt}
              </div>
            ))}
          </div>
        )}

        {isOpen && search && filteredOptions.length === 0 && (
          <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-20 rounded-lg border border-slate-200 bg-white shadow-lg py-2 px-2.5 text-xs text-slate-400">
            No matches
          </div>
        )}
      </div>
    </div>
  );
}

export default function Customers() {
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const filterMenuRef = useRef(null);
  const amMenuRef = useRef(null);

  // API Data & Lifecycle States
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { toast, showToast, hideToast } = useToast();
  const { confirm, confirmState, handleConfirm, handleCancel } = useConfirm();

  // UI States
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeApprovalFilter, setActiveApprovalFilter] = useState("All");
  const [activeDropdownId, setActiveDropdownId] = useState(null);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [amMenuOpen, setAmMenuOpen] = useState(false);

  // Account Manager scoping (admin only — see below)
  const [accountManagerFilter, setAccountManagerFilter] = useState([]);
  const [viewAll, setViewAll] = useState(false);

  // Extra filter-panel fields (beyond Account Manager)
  const [customerNameFilter, setCustomerNameFilter] = useState([]);
  const [cinFilter, setCinFilter] = useState("");
  const [gstFilter, setGstFilter] = useState("");
  const [addressFilter, setAddressFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [pincodeFilter, setPincodeFilter] = useState("");

  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = (currentUser.department || "").toLowerCase() === "admin";

  // Arriving from a specific user's dashboard (Users list -> that user's
  // dashboard -> a stat card) pre-selects that person as the Account
  // Manager instead of showing a blank page.
  useEffect(() => {
    if (location.state?.accountManager) {
      setAccountManagerFilter([location.state.accountManager]);
      setViewAll(false);
    } else if (location.state?.viewAll) {
      setAccountManagerFilter([]);
      setViewAll(true);
    }
  }, [location.state]);

  // Admin-only: bypass the "pick an Account Manager first" restriction and
  // show every customer at once. Acts as a toggle — clicking again while
  // active ("Hide All") turns it back off and returns to the blank state.
  const handleViewAll = () => {
    setAccountManagerFilter([]);
    setViewAll((prev) => !prev);
    setSelected([]);
  };

  // Sorting & Pagination
  const [sortField, setSortField] = useState("cust_id");
  const [sortOrder, setSortOrder] = useState("asc"); // "asc" | "desc"
  const [currentPage, setCurrentPage] = useState(1);
  // const itemsPerPage = 10;
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const handleItemsPerPageChange = (event) => {
  setItemsPerPage(Number(event.target.value));
  setCurrentPage(1);
  setSelected([]);
};

  const NUMERIC_FIELDS = ["cust_id", "cust_admin_approval"];

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  /* FETCH ALL CUSTOMERS FROM API */
  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await api.get("customer/all");
      const fetchedData = response.data?.data || [];
      setCustomers(Array.isArray(fetchedData) ? fetchedData : []);
      setError(null);
    } catch (err) {
      console.error("Error fetching customers:", err);
      setError("Failed to load customers. Please try again later.");
      showToast("Error fetching customer data.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  /* FILTER BY SEARCH & APPROVAL STATUS */
  const accountManagerOptions = Array.from(
    new Set(customers.map((c) => c.account_manager).filter(Boolean))
  ).sort();
  const customerNameOptions = Array.from(
    new Set(customers.map((c) => c.cust_name).filter(Boolean))
  ).sort();

  const isAdminBlankState = isAdmin && !viewAll && accountManagerFilter.length === 0;

  const filtered = (isAdminBlankState ? [] : customers).filter((account) => {
    const matchesSearch =
      (account.cust_name?.toLowerCase() || "").includes(search.toLowerCase()) ||
      String(account.cust_id || "").toLowerCase().includes(search.toLowerCase());

    // Evaluate structural DB indicator mapped to semantic text matching
    const statusText = Number(account.cust_admin_approval) === 1 ? "Approved" : "Denied";
    
    const matchesApproval =
      activeApprovalFilter === "All" 
        ? true 
        : statusText === activeApprovalFilter;

    const matchesAccountManager =
      accountManagerFilter.length === 0 || accountManagerFilter.includes(account.account_manager || "");

    const matchesCustomerName =
      customerNameFilter.length === 0 || customerNameFilter.includes(account.cust_name || "");

    const matchesCin =
      !cinFilter || (account.cust_cin || "").toLowerCase().includes(cinFilter.toLowerCase());

    const matchesGst =
      !gstFilter || (account.cust_gst || "").toLowerCase().includes(gstFilter.toLowerCase());

    const matchesAddress =
      !addressFilter || (account.cust_address || "").toLowerCase().includes(addressFilter.toLowerCase());

    const matchesCity =
      !cityFilter || (account.cust_city || "").toLowerCase().includes(cityFilter.toLowerCase());

    const matchesState =
      !stateFilter || (account.cust_state || "").toLowerCase().includes(stateFilter.toLowerCase());

    const matchesPincode =
      !pincodeFilter || (account.cust_pincode || "").toLowerCase().includes(pincodeFilter.toLowerCase());

    return (
      matchesSearch &&
      matchesApproval &&
      matchesAccountManager &&
      matchesCustomerName &&
      matchesCin &&
      matchesGst &&
      matchesAddress &&
      matchesCity &&
      matchesState &&
      matchesPincode
    );
  }).sort((a, b) => {
    let valA = a[sortField];
    let valB = b[sortField];

    if (NUMERIC_FIELDS.includes(sortField)) {
      valA = Number(valA) || 0;
      valB = Number(valB) || 0;
      return sortOrder === "asc" ? valA - valB : valB - valA;
    }

    valA = String(valA || "").toLowerCase();
    valB = String(valB || "").toLowerCase();
    if (valA < valB) return sortOrder === "asc" ? -1 : 1;
    if (valA > valB) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const selectedCustomers = selected.map((i) => filtered[i]);

  const activeFilterCount =
    customerNameFilter.length +
    (cinFilter ? 1 : 0) +
    (gstFilter ? 1 : 0) +
    (addressFilter ? 1 : 0)+
    (cityFilter ? 1 : 0)+
    (stateFilter ? 1 : 0)+
    (pincodeFilter ? 1 : 0);

  /* PAGINATION (based on sorted + filtered results) */
  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paginatedCustomers = filtered
    .map((account, index) => ({ account, index }))
    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    search,
    activeApprovalFilter,
    accountManagerFilter,
    customerNameFilter,
    cinFilter,
    gstFilter,
    addressFilter,
    sortField,
    sortOrder,
  ]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  /* Outside click listener to close dropdown */
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdownId(null);
      }
      if (
        filterMenuOpen &&
        filterMenuRef.current &&
        !filterMenuRef.current.contains(event.target)
      ) {
        setFilterMenuOpen(false);
      }
      if (
        amMenuOpen &&
        amMenuRef.current &&
        !amMenuRef.current.contains(event.target)
      ) {
        setAmMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [filterMenuOpen, amMenuOpen]);

  /* SELECT */
  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected(filtered.map((_, i) => i));
    } else {
      setSelected([]);
    }
  };

  const toggleRow = (index) => {
    setSelected((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  /* LIVE API ACTIONS */
  const handleExport = async () => {
    if (selectedCustomers.length === 0) return;
    try {
      const customerIds = selectedCustomers.map(c => c.cust_id);
      await api.post("/customers/export", { ids: customerIds });
      showToast("Export compiled successfully.", "success");
    } catch (err) {
      console.error("Export failed:", err);
      showToast("Failed to compile export.", "error");
    }
  };

  const handleDeleteAccount = async (account) => {
    if (!(await confirm(`Are you sure you want to delete information for ${account.cust_name}?`))) {
      return;
    }
    try {
      await api.delete(`customer/delete/${account.cust_id}`);
      showToast("Account deleted successfully.", "success");
      setSelected([]); 
      fetchCustomers(); 
    } catch (err) {
      console.error("Delete failed:", err);
      showToast("Failed to delete account.", "error");
    }
  };

  return (
    <div className="ml-[60px] pt-6 p-3 sm:p-6 bg-[#F1F5F9] min-h-screen overflow-x-hidden mt-12 md:h-[calc(100vh-3rem)] md:min-h-0 md:flex md:flex-col md:overflow-hidden">
      <Toast toast={toast} onClose={hideToast} />
      <ConfirmDialog confirmState={confirmState} onConfirm={handleConfirm} onCancel={handleCancel} />

      {/* HEADER */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800">Customers</h1>
          <p className="text-sm text-slate-500 mt-1">
            Customers &gt; List | Total Customers: {filtered.length}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <>
              <div className="relative" ref={amMenuRef}>
                <button
                  onClick={() => setAmMenuOpen((prev) => !prev)}
                  className={`relative flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer transition ${
                    amMenuOpen || accountManagerFilter.length > 0
                      ? "border-blue-300 bg-blue-50 text-blue-700"
                      : "border-slate-200 bg-white hover:bg-slate-50"
                  }`}
                >
                  <Users size={14} />
                  Account Manager
                  {accountManagerFilter.length > 0 && (
                    <span className="flex items-center justify-center rounded-full bg-blue-600 text-white text-[10px] font-bold w-4.5 h-4.5 min-w-[18px] min-h-[18px] px-1">
                      {accountManagerFilter.length}
                    </span>
                  )}
                </button>

                {amMenuOpen && (
                  <div className="absolute right-0 top-[calc(100%+6px)] z-30 w-64 max-w-[90vw] rounded-xl border border-slate-200 bg-white shadow-lg p-3">
                    <MultiSelectFilterField
                      label="Account Manager"
                      placeholder="Type to search..."
                      options={accountManagerOptions}
                      selectedValues={accountManagerFilter}
                      onChange={(value) => {
                        setViewAll(false);
                        setAccountManagerFilter(value);
                        setSelected([]);
                      }}
                      onClear={() => {
                        setAccountManagerFilter([]);
                        setSelected([]);
                      }}
                    />
                  </div>
                )}
              </div>

              <button
                onClick={handleViewAll}
                className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  viewAll
                    ? "border-blue-300 bg-blue-50 text-blue-700"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {viewAll ? "Hide All" : "View All"}
              </button>
              </>
              )}

              <div className="relative" ref={filterMenuRef}>
                <button
                  onClick={() => setFilterMenuOpen((prev) => !prev)}
                  className={`relative flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer transition ${
                    filterMenuOpen || activeFilterCount > 0
                      ? "border-blue-300 bg-blue-50 text-blue-700"
                      : "border-slate-200 bg-white hover:bg-slate-50"
                  }`}
                >
                  <Filter size={14} />
                  Filter
                  {activeFilterCount > 0 && (
                    <span className="flex items-center justify-center rounded-full bg-blue-600 text-white text-[10px] font-bold w-4.5 h-4.5 min-w-[18px] min-h-[18px] px-1">
                      {activeFilterCount}
                    </span>
                  )}
                </button>

                {filterMenuOpen && (
                  <div className="absolute right-0 top-[calc(100%+6px)] z-30 w-72 max-w-[90vw] max-h-[75vh] overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg p-3">
                    <div className="flex items-center justify-between mb-2 sticky -top-3 -mt-3 -mx-3 px-3 pt-3 pb-2 bg-white border-b border-slate-100 z-10">
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                        Filters
                      </p>
                      {activeFilterCount > 0 && (
                        <button
                          onClick={() => {
                            setCustomerNameFilter([]);
                            setCinFilter("");
                            setGstFilter("");
                            setAddressFilter("");
                            setCityFilter("");
                            setStateFilter("");
                            setPincodeFilter("");
                          }}
                          className="text-xs font-semibold text-blue-600 hover:text-blue-700 cursor-pointer"
                        >
                          Clear all
                        </button>
                      )}
                    </div>

                    <MultiSelectFilterField
                      label="Customer Name"
                      placeholder="Type to search..."
                      options={customerNameOptions}
                      selectedValues={customerNameFilter}
                      onChange={(value) => {
                        setCustomerNameFilter(value);
                        setSelected([]);
                      }}
                    />

                    <div className="mb-3">
                      <p className="mb-1.5 text-xs font-bold text-slate-600">CIN</p>
                      <input
                        type="text"
                        placeholder="Contains..."
                        value={cinFilter}
                        onChange={(event) => { setCinFilter(event.target.value); setSelected([]); }}
                        className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="mb-3">
                      <p className="mb-1.5 text-xs font-bold text-slate-600">GST</p>
                      <input
                        type="text"
                        placeholder="Contains..."
                        value={gstFilter}
                        onChange={(event) => { setGstFilter(event.target.value); setSelected([]); }}
                        className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <p className="mb-1.5 text-xs font-bold text-slate-600">Address</p>
                      <input
                        type="text"
                        placeholder="Contains..."
                        value={addressFilter}
                        onChange={(event) => { setAddressFilter(event.target.value); setSelected([]); }}
                        className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <p className="mb-1.5 text-xs font-bold text-slate-600">City</p>
                      <input
                        type="text"
                        placeholder="Contains..."
                        value={cityFilter}
                        onChange={(event) => { setCityFilter(event.target.value); setSelected([]); }}
                        className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <p className="mb-1.5 text-xs font-bold text-slate-600">State</p>
                      <input
                        type="text"
                        placeholder="Contains..."
                        value={stateFilter}
                        onChange={(event) => { setStateFilter(event.target.value); setSelected([]); }}
                        className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <p className="mb-1.5 text-xs font-bold text-slate-600">Pincode</p>
                      <input
                        type="text"
                        placeholder="Contains..."
                        value={pincodeFilter}
                        onChange={(event) => { setPincodeFilter(event.target.value); setSelected([]); }}
                        className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                )}
              </div>
            
          

          <button
            onClick={() => setOpen(true)}
            className="w-full sm:w-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition"
          >
            + Add Customer
          </button>

          {open && (
            <AddAccountModal
              onClose={() => {
                setOpen(false);
                fetchCustomers();
              }}
            />
          )}
        </div>
      </div>

      {/* FILTER + SEARCH */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <input
            type="text"
            placeholder="Search Customers by Name or Customer ID..."
            className="w-full sm:flex-1 sm:min-w-[250px] px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setSelected([]); }}
          />

          {["All", "Approved", "Denied"].map((status) => (
            <button
              key={status}
              onClick={() => { setActiveApprovalFilter(status); setSelected([]); }}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                activeApprovalFilter === status
                  ? status === "Approved" ? "bg-green-600 text-white"
                    : status === "Denied" ? "bg-red-600 text-white"
                    : "bg-blue-600 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* TABLE / LOADING / ERROR STATES */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col md:flex-1 md:min-h-0">
        {loading && !syncing ? (
          <div className="flex-1 py-16 flex flex-col items-center justify-center text-slate-500 text-sm font-medium gap-2">
            <Loader2 size={24} className="animate-spin text-blue-600" />
            <span>Loading Customers...</span>
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center px-4 py-16 text-center text-red-500 text-sm font-medium">
            {error}
          </div>
        ) : (
          <div className={`overflow-auto max-h-[70vh] md:max-h-none min-h-0 ${filtered.length === 0 ? "flex-none" : "flex-1"}`}>
          <table className="w-full min-w-[900px] text-sm [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-slate-50 [&_thead_th]:shadow-[inset_0_-1px_0_#e2e8f0]">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500 border-b border-slate-200">
              <tr>
                <th className="p-3 w-12 text-center">
                  <input
                    type="checkbox"
                    onChange={toggleSelectAll}
                    checked={filtered.length > 0 && selected.length === filtered.length}
                    className="cursor-pointer"
                  />
                </th>
                <th
                  className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                  onClick={() => handleSort("cust_id")}
                >
                  <span className="inline-flex items-center gap-1">
                    Customer Id
                    {sortField === "cust_id" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                  </span>
                </th>
                <th
                  className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                  onClick={() => handleSort("cust_name")}
                >
                  <span className="inline-flex items-center gap-1">
                    Customer Name
                    {sortField === "cust_name" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                  </span>
                </th>
                <th
                  className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                  onClick={() => handleSort("cust_cin")}
                >
                  <span className="inline-flex items-center gap-1">
                    CIN
                    {sortField === "cust_cin" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                  </span>
                </th>
                <th
                  className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                  onClick={() => handleSort("cust_gst")}
                >
                  <span className="inline-flex items-center gap-1">
                    GST
                    {sortField === "cust_gst" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                  </span>
                </th>
                <th
                  className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                  onClick={() => handleSort("cust_admin_approval")}
                >
                  <span className="inline-flex items-center gap-1">
                    Approval
                    {sortField === "cust_admin_approval" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                  </span>
                </th>
                <th
                  className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                  onClick={() => handleSort("account_manager")}
                >
                  <span className="inline-flex items-center gap-1">
                    Account Manager
                    {sortField === "account_manager" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                  </span>
                </th>
                <th className="p-3 w-16 text-center">Action</th>
              </tr>
            </thead>

            <tbody>
              {paginatedCustomers.map(({ account, index }) => {
                const isApproved = Number(account.cust_admin_approval) === 1;
                
                return (
                  <tr
                    key={account.cust_id || index}
                    onClick={() => navigate(`/Customers/${account.cust_id}`)}
                    className="border-b border-slate-100 hover:bg-slate-50/80 transition cursor-pointer"
                  >
                    <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.includes(index)}
                        onChange={() => toggleRow(index)}
                        className="cursor-pointer"
                      />
                    </td>

                    <td className="p-3 font-semibold text-blue-600">
                      {account.cust_id }

                      </td>

                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center flex-shrink-0">
                          <Building2 size={16} />
                        </div>
                        <div>
                          <div className="font-semibold text-slate-800">{account.cust_name}</div>
                          <div className="text-xs text-slate-400 font-medium">{account.cust_address || "No Address Added"}</div>
                        </div>
                      </div>
                    </td>

                    <td className="p-3 font-mono text-xs text-slate-600 tracking-tight">{account.cust_cin || "—"}</td>
                    <td className="p-3 font-mono text-xs text-slate-600">{account.cust_gst || "—"}</td>

                    {/* Transformed Approval Status Column */}
                    <td className="p-3">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full border ${
                          isApproved
                            ? "bg-green-50 text-green-700 border-green-200"
                            : "bg-red-50 text-red-700 border-red-200"
                        }`}
                      >
                        {isApproved ? (
                          <CheckCircle2 size={12} className="text-green-600" />
                        ) : (
                          <XCircle size={12} className="text-red-600" />
                        )}
                        {isApproved ? "Approved" : "Denied"}
                      </span>
                    </td>

                    {/* Manager resolved directly to readable name string field */}
                    <td className="p-3">
                      <div className="text-sm font-medium text-slate-700">{account.account_manager || "—"}</div>
                      <div className="text-xs text-slate-400 font-medium mt-0.5">{account.user_email || "—"}</div>
                    </td>

                    {/* ACTION MENU */}
                    <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="relative inline-block text-left" ref={activeDropdownId === index ? dropdownRef : null}>
                        <button
                          className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-800 transition"
                          onClick={() => setActiveDropdownId(activeDropdownId === index ? null : index)}
                        >
                          <MoreHorizontal size={16} />
                        </button>

                        {activeDropdownId === index && (
                          <div className="absolute right-0 mt-1.5 w-40 bg-white border border-slate-200 rounded-xl shadow-xl z-50 py-1.5 animate-in fade-in slide-in-from-top-1 duration-100">
                            <button
                              className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 text-left"
                              onClick={() => {
                                setActiveDropdownId(null);
                                navigate(`/Customers/${account.cust_id}`);
                              }}
                            >
                              <Pencil size={13} className="text-blue-600" />
                              <span>View Details</span>
                            </button>

                            {/* <div className="h-[1px] bg-slate-100 my-1"></div>

                            <button
                              className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 text-left"
                              onClick={() => {
                                setActiveDropdownId(null);
                                handleDeleteAccount(account);
                              }}
                            >
                              <Trash2 size={13} />
                              <span>Delete Account</span>
                            </button> */}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="flex-1 flex items-center justify-center py-16 text-center text-slate-500 text-sm font-medium px-4">
            {isAdminBlankState
              ? "Select an Account Manager above (or click View All) to see customers."
              : "No Customers Found"}
          </div>
        )}
      </div>

      {/* PAGINATION */}
    {/* PAGINATION */}
{!loading && filtered.length > 0 && (
  <div className="flex flex-wrap items-center justify-between gap-3 mt-3 px-2 shrink-0">
    {/* Records per page */}
    <div className="flex items-center gap-2 text-xs text-slate-600">
      <span className="font-medium">Rows per page:</span>

      <select
        value={itemsPerPage}
        onChange={handleItemsPerPageChange}
        className="
          rounded-lg
          border border-slate-200
          bg-white
          px-3 py-1.5
          text-xs
          font-medium
          text-slate-700
          outline-none
          cursor-pointer
          shadow-sm
          focus:border-blue-500
          focus:ring-2
          focus:ring-blue-100
        "
      >
        <option value={10}>10</option>
        <option value={25}>25</option>
        <option value={50}>50</option>
        <option value={100}>100</option>
      </select>
    </div>

    {/* Page navigation */}
    <div className="inline-flex items-center gap-3 bg-white border border-slate-200 rounded-full px-3 py-1.5 text-xs text-slate-600 shadow-sm">
      <span className="text-slate-400">
        {(currentPage - 1) * itemsPerPage + 1}-
        {Math.min(currentPage * itemsPerPage, filtered.length)} of{" "}
        {filtered.length}
      </span>

      <div className="h-4 w-px bg-slate-200" />

      <button
        type="button"
        onClick={() =>
          setCurrentPage((previousPage) =>
            Math.max(1, previousPage - 1)
          )
        }
        disabled={currentPage === 1}
        className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
        aria-label="Previous page"
      >
        <ChevronLeft size={14} />
      </button>

      <span className="font-medium text-slate-700">
        {currentPage} / {totalPages}
      </span>

      <button
        type="button"
        onClick={() =>
          setCurrentPage((previousPage) =>
            Math.min(totalPages, previousPage + 1)
          )
        }
        disabled={currentPage === totalPages}
        className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
        aria-label="Next page"
      >
        <ChevronRight size={14} />
      </button>
    </div>
  </div>
)}

      {/* OVERLAY */}
      {drawerOpen && (
        <div onClick={() => setDrawerOpen(false)} className="fixed inset-0 bg-black/30 z-40" />
      )}

      {/* SIDEBAR DRAWER */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-[320px] bg-white shadow-xl z-50 transform transition-transform duration-300 overflow-y-auto ${
          drawerOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="p-4 border-b font-semibold text-slate-800">
          {selected.length > 1 ? "Bulk Summary" : "Account Details"}
        </div>

        <div className="p-4 space-y-4">
          {selected.length > 1 && (
            <>
              {selectedCustomers.map((account, i) => (
                <div key={i} className="text-xs text-slate-700">• {account?.cust_name}</div>
              ))}
              <button
                onClick={handleExport}
                className="w-full bg-blue-600 text-white text-xs font-semibold py-2 rounded-lg mt-4 shadow-sm hover:bg-blue-700 transition flex items-center justify-center gap-1.5"
              >
                <Download size={13} /> Export Selected ({selected.length})
              </button>
            </>
          )}

          {selected.length === 1 && selectedCustomers[0] && (
            <>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center">
                  <Building2 size={20} />
                </div>
                <div>
                  <div className="font-semibold text-slate-800">{selectedCustomers[0].cust_name}</div>
                  <div className="text-xs text-slate-500">ID: {selectedCustomers[0].cust_id}</div>
                </div>
              </div>
              <div className="space-y-1.5 pt-2 text-xs">
                <p><span className="text-slate-400 font-medium">CIN:</span> <span className="font-mono">{selectedCustomers[0].cust_cin || "—"}</span></p>
                <p><span className="text-slate-400 font-medium">GST:</span> <span className="font-mono">{selectedCustomers[0].cust_gst || "—"}</span></p>
                
                {/* Transformed Drawer Preview Indicators */}
                <p>
                  <span className="text-slate-400 font-medium">Approval:</span>{" "}
                  <span className={`font-semibold ${Number(selectedCustomers[0]?.cust_admin_approval) === 1 ? "text-green-600" : "text-red-500"}`}>
                    {Number(selectedCustomers[0]?.cust_admin_approval) === 1 ? "Approved" : "Denied"}
                  </span>
                </p>              
                <p><span className="text-slate-400 font-medium">Manager:</span> <span className="font-medium text-slate-700">{selectedCustomers[0].account_manager || "—"}</span></p>
              </div>
              <div className="flex gap-4 mt-4 text-slate-500">
                <Phone size={18} className="cursor-pointer hover:text-slate-800 transition" />
                <MessageCircle size={18} className="cursor-pointer hover:text-slate-800 transition" />
              </div>
            </>
          )}

          {selected.length === 0 && (
            <p className="text-slate-400 text-xs font-medium">No account selected</p>
          )}
        </div>
      </div>
    </div>
  );
}